import "./Sec.css";
import {
  calculateCircle,
  calculateCircleAsync,
  calculateCircleAsyncSmart,
  calculateCircleSmart,
} from "./circleAlgos";
import { Layer, Stage, Circle, Text } from "react-konva";
import { useEffect, useRef, useState } from "react";
import { Button, ButtonGroup, Slider, Switch } from "@mui/material";
import { MdAddCircle, MdDelete } from "react-icons/md";
import { GrPowerReset } from "react-icons/gr";
import { GiRabbit, GiSnail } from "react-icons/gi";
import { AiOutlineFastForward } from "react-icons/ai";
import { theme } from "./siteTheme";
import { ThemeProvider } from "@mui/material/styles";
import Alert from "@mui/material/Alert";

const Sec = () => {
  // alert if mobile device
  const [width, setWidth] = useState(window.innerWidth);

  function handleWindowSizeChange() {
    setWidth(window.innerWidth);
  }
  useEffect(() => {
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  const isMobile = width <= 768;

  // logic for canvas size
  const canvasRef = useRef();
  const [canvasWidth, setCanvasWidth] = useState(0);
  const [canvasHeight, setCanvasHeight] = useState(0);
  const updateSize = () => {
    setCanvasWidth(canvasRef.current.offsetWidth);
    setCanvasHeight(canvasRef.current.offsetHeight);
  };
  useEffect(() => {
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => {
      window.removeEventListener("resize", updateSize);
    };
  }, []);

  // add/delete logic
  const [isAdd, setIsAdd] = useState(false);
  const [isDelete, setIsDelete] = useState(false);

  const toggleMode = (mode) => {
    setCircle({ ...circle, r: 0 });
    resetMultiplicities();
    if (mode === "add") {
      setIsAdd(!isAdd);
      setIsDelete(false);
    } else if (mode === "delete") {
      setIsDelete(!isDelete);
      setIsAdd(false);
    } else {
      setIsDelete(false);
      setIsAdd(false);
    }
  };

  // mouse position on canvas
  const [mousePos, setMousePos] = useState({
    x: 0,
    y: 0,
  });

  const onMouseMove = (e) => {
    setMousePos({
      x: e.clientX - 20,
      y: e.clientY - 100,
    });
  };

  // manage points
  const [curId, setCurId] = useState(0);
  const [points, setPoints] = useState([]);
  const [pointsLength, setPointsLength] = useState(0);
  const addPoint = () => {
    const newPoint = { id: curId, x: mousePos.x, y: mousePos.y };
    const newText = { ...newPoint, x: mousePos.x - 4, y: mousePos.y - 20 };
    let len = points.length;
    for (let i = 0; i < len; i++) {
      if (newPoint.x === points[i].x && newPoint.y === points[i].y) {
        return;
      }
    }
    setMultiplicities([...multiplicities, 1]);
    setCurId(curId + 1);
    setPoints([...points, newPoint]);
    setTexts([...texts, newText]);
    setPointsLength(pointsLength + 1);
  };
  const deletePoint = (id) => {
    setPoints(points.filter((point) => point.id !== id));
    setTexts(texts.filter((text) => text.id !== id));
    setPointsLength(pointsLength - 1);
  };

  const updatePoint = (id) => {
    // update coordinates of point dragged
    setPoints(
      points.map((point) =>
        id === point.id ? { ...point, x: mousePos.x, y: mousePos.y } : point
      )
    );
    setTexts(
      texts.map((text) =>
        id === text.id
          ? { ...text, x: mousePos.x - 4, y: mousePos.y - 20 }
          : text
      )
    );
  };

  // reset canvas
  const stop = useRef(false);
  const resetCanvas = () => {
    stop.current = true;
    setPoints([]);
    setPointsLength(0);
    resetMultiplicities([]);
    setTexts([]);
    setCurId(0);
    setIterations(0);
    toggleMode("reset");
    setCircle([{ ...circle, r: 0 }]);
    setIsCalculating(false);
  };

  // circle states
  const [iterations, setIterations] = useState(0);
  const [circle, setCircle] = useState({ x: 0, y: 0, r: 0 });
  const [isCalculating, setIsCalculating] = useState(false);

  const calcCircle = (delay, isAnimation) => {
    console.log("points", points);
    stop.current = false;
    toggleMode("reset");
    if (isSmart) {
      if (isAnimation) {
        calculateCircleAsyncSmart(
          points,
          setCircle,
          setIterations,
          numPoints,
          delay,
          stop,
          multiplicities.fill(1),
          setMultiplicities,
          setIsCalculating
        );
      } else {
        calculateCircleSmart(
          points,
          setCircle,
          setIterations,
          numPoints,
          multiplicities.fill(1),
          setMultiplicities
        );
      }
    } else {
      if (isAnimation) {
        calculateCircleAsync(
          points,
          setCircle,
          setIterations,
          numPoints,
          delay,
          stop,
          setIsCalculating
        );
      } else {
        calculateCircle(points, setCircle, setIterations, numPoints);
      }
    }
  };

  // number of points per guess
  const [numPoints, setNumPoints] = useState(3);

  // extra logic for smart mode
  const [isSmart, setIsSmart] = useState(false);
  const [texts, setTexts] = useState([]);

  const [multiplicities, setMultiplicities] = useState([]);

  const resetMultiplicities = () => {
    setMultiplicities(multiplicities.map((cur) => 1));
  };

  return (
    <div className="sec-site-container">
      {isMobile && (
        <div style={{ marginRight: 20 }}>
          <Alert severity="error">
            This project does not work on mobile yet!
          </Alert>
        </div>
      )}

      <div
        className="sec-canvas-container"
        ref={canvasRef}
        onMouseMove={onMouseMove}
        onClick={isAdd ? addPoint : () => void 0}
      >
        <Stage width={canvasWidth} height={canvasHeight}>
          <Layer>
            {points.map((point) => (
              <Circle
                x={point.x}
                y={point.y}
                radius={5}
                fill="black"
                key={point.id}
                draggable
                onMouseOver={() => isDelete && deletePoint(point.id)}
                onDragEnd={() => {
                  updatePoint(point.id);
                  setIsAdd(true);
                }}
                onDragStart={() => setIsAdd(false)}
                name={"test"}
              />
            ))}
            {isSmart &&
              texts.map((text) => (
                <Text
                  x={text.x}
                  y={text.y}
                  text={multiplicities[text.id]}
                  key={text.id}
                />
              ))}
            <Circle
              x={circle.x}
              y={circle.y}
              radius={circle.r}
              strokeWidth={2}
              stroke="black"
              key={0}
            />
          </Layer>
        </Stage>
      </div>
      <div className="sec-side-container">
        <div className="sec-controls-container">
          <ThemeProvider theme={theme}>
            <div className="sec-controls-calc">
              <ButtonGroup fullWidth color="primary">
                <Button
                  onClick={() => calcCircle(250, true)}
                  disabled={isCalculating || pointsLength < 3}
                  startIcon={<GiSnail />}
                  sx={{ color: "black" }}
                >
                  slow
                </Button>
                <Button
                  onClick={() => calcCircle(100, true)}
                  disabled={isCalculating || pointsLength < 3}
                  sx={{ color: "black" }}
                >
                  medium
                </Button>
                <Button
                  onClick={() => calcCircle(0, true)}
                  disabled={isCalculating || pointsLength < 3}
                  startIcon={<GiRabbit />}
                  sx={{ color: "black" }}
                >
                  fast
                </Button>
                <Button
                  onClick={() => {
                    calcCircle(0, false);
                  }}
                  disabled={isCalculating || pointsLength < 3}
                  startIcon={<AiOutlineFastForward />}
                  sx={{ color: "black" }}
                >
                  Instant
                </Button>
              </ButtonGroup>
            </div>
            <div className="sec-controls-switch">
              Naive
              <Switch
                checked={isSmart}
                onChange={(e) => setIsSmart(!isSmart)}
                disabled={isCalculating}
              />
              Smart
            </div>
            <div className="sec-controls-info">
              <div>Iterations: {iterations}</div>
              <div>|N| = {numPoints}</div>
            </div>
            <div className="sec-slider-container">
              <div className="sec-slider">
                <Slider
                  value={numPoints}
                  min={3}
                  max={Math.max(pointsLength, 3)}
                  step={1}
                  disabled={pointsLength < 4}
                  onChange={(e) => setNumPoints(e.target.value)}
                  valueLabelDisplay="auto"
                />
              </div>
            </div>
            <div className="sec-controls-btns">
              <Button
                variant={isAdd ? "contained" : "outlined"}
                startIcon={<MdAddCircle />}
                disabled={isCalculating}
                onClick={() => toggleMode("add")}
                color="success"
                fullWidth
              >
                Add
              </Button>
              <Button
                variant={isDelete ? "contained" : "outlined"}
                startIcon={<MdDelete />}
                disabled={isCalculating}
                onClick={() => toggleMode("delete")}
                color="error"
                fullWidth
              >
                Delete
              </Button>
            </div>
          </ThemeProvider>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              startIcon={<GrPowerReset />}
              onClick={resetCanvas}
              color="inherit"
            >
              Reset
            </Button>
          </div>
        </div>
        <div className="sec-legend">
          <p>
            Press <strong>add</strong> and click on the canvas to add points.
          </p>
          <p>
            Press <strong>delete</strong> and hover above a point to delete it.
          </p>
          <p>
            Press <strong>reset</strong> to clear the canvas.
          </p>
          <p>
            Switch between <strong>naive</strong> and <strong>smart</strong>{" "}
            implementation of the algorithm.
          </p>
          <p>
            Use the slider to adjust the value of <strong>|N|</strong> that
            determines number of points chosen per iteration.
          </p>
        </div>
        <div className="sec-infocard">
          <h1>Smallest enclosing circle</h1>
          <p>
            The objective of the smallest enclosing circle algorithm, is, as the
            name suggests, to find the smallest circle that contains all given
            points on a canvas.
          </p>
          <p>
            In this application the two given implementations are{" "}
            <strong>randomized algorithms</strong>.
          </p>
          <h2>Naive algorithm</h2>
          <p>
            Let P be the array containing all points on the canvas and C(P) the
            corresponding smallest enclosing circle. This algorithm exploits
            following lemma:
          </p>
          <p className="sec-lemma">
            For any finite P on a canvas with |P| &#8805; 3 there exists a
            subset Q &#8838; P, such that |Q|=3 and smallest enclosing circle of
            Q is equal to P.
          </p>
          <p>
            In every iteration of the algorithm we randomly select |N| points of
            P and calculate C(N). If all points are contained then we return
            this circle. This gives us following pseudocode:
          </p>
          <div className="sec-pseudocode">
            <p>
              <strong>while</strong>
            </p>
            <div className="sec-indent">
              <p>choose Q &#8838; P with |Q| = |N| randomly </p>
              <p>calculate C(Q)</p>
              <p>
                <strong>if</strong> P contained in C(Q) <strong>then</strong>
              </p>
              <div className="sec-indent">
                <p>
                  <strong>return</strong> C(Q)
                </p>
              </div>
            </div>
          </div>
          <h2>Smart algorithm</h2>
          <p>
            The naive algorithm is slow as it guesses a random circle with even
            distribution every iteration. The smart algorithm is an improved
            randomized algorithm that builds on the idea of the naive
            implementation.
          </p>
          <p>
            The main improvement comes from doubling points that lie outside
            C(Q) for a random Q &#8838; P with |Q| = |N| in every iteration.
            Following pseudocode showcases the algorithm:
          </p>
          <div className="sec-pseudocode">
            <p>
              <strong>while</strong>
            </p>
            <div className="sec-indent">
              <p>choose Q &#8838; P with |Q| = |N| randomly </p>
              <p>calculate C(Q)</p>
              <p>
                <strong>if</strong> P contained in C(Q) <strong>then</strong>
              </p>
              <div className="sec-indent">
                <p>
                  <strong>return</strong> C(Q)
                </p>
              </div>
              <p>double points in P outside of C(Q)</p>
            </div>
          </div>
          <p>
            Probabilistic runtime analysis yields an expected runtime of O(n log
            n) for the smart algorithm.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Sec;
